import React, { useState, useEffect } from "react";
import { useParams, Link, Outlet, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import useApi from "../../hooks/useApi";
import usePagination from "../../hooks/usePagination";
import useReload from "../../hooks/useReload";
import { FilterBar } from "../../widgets";
import moment from "moment";
import useSortableTableHeader from "../../hooks/useSortableTableHeader";
import PolicyItemStatus, { activeStatus } from "../../widgets/PolicyItemStatus";

const PolicyList = (props) => {
  const { scope = "" } = props;
  return props?.mini ? (
    <PolicyListMini scope={scope} />
  ) : (
    <PolicyListHead>
      <PolicyListMini headless={true} scope={scope} />
    </PolicyListHead>
  );
};

const PolicyListHead = (props) => {
  return (
    <section id="content" className="content">
      <div className="content__header content__boxed">
        <div className="content__wrap">
          {/* <!-- Breadcrumb --> */}
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0">
              <li className="breadcrumb-item">
                <Link to="/desktop">Home</Link>
              </li>
              <li className="breadcrumb-item">
                <Link to="/policies">Policies</Link>
              </li>
              <li className="breadcrumb-item active" aria-current="page">
                List
              </li>
            </ol>
          </nav>
          {/* <!-- END : Breadcrumb --> */}

          <h1 className="page-title mb-0 mt-2">Policies List</h1>

          <p className="lead">List of all policies with insurrance companies</p>

          <div className="d-md-flex align-items-baseline my-3">
            <Link to="/policies/add" className="btn btn-info hstack gap-2 mb-3">
              <i className="demo-psi-add fs-4"></i>
              <span className="vr"></span>
              Add Policy
            </Link>
            <FilterBar />
          </div>
        </div>
      </div>
      <div className="content__boxed">
        <div className="content__wrap">{props.children}</div>
      </div>
    </section>
  );
};

const PolicyListMini = (props) => {
  const { reload } = useReload();
  const { id = "" } = useParams();
  const { scope = "" } = props;
  const { Paginate, searchParamString } = usePagination();
  const { api, abortSignal, auth } = useApi();
  const navigate = useNavigate();
  const { TableHeader } = useSortableTableHeader();

  const getPolicies = async (searchString) => {
    try {
      const url = `/policies${scope}${id}${searchString}`;
      const { data } = await api.list(url);
      return data;
    } catch (error) {
      throw new Error("failed to fetch records");
    }
  };

  const { data: policies } = useQuery(
    ["policies", searchParamString, auth],
    ({ queryKey }) => getPolicies(queryKey[1])
  );

  // console.log(policies);
  let clientLink = (scope === "/clients/" && (id ? `${scope}${id}` : "")) || "";

  const headers = [
    { label: "Client", sort: true, field: "Client.name" },
    { label: "Items", sort: true, field: "itemsCount" },
    { label: "Expiring", sort: true, field: "expiringCount" },
    { label: "Expired", sort: true, field: "expiredCount" },
    { label: "Created", sort: true, field: "createdAt" },
    // { label: "Info" },
    // { label: "Status", className: "text-center", sort: true },
  ];

  return (
    <>
      {!props?.headless && (
        <>
          <h2 className="mb-0 mt-2">Policies</h2>
          <div className="d-md-flex align-items-baseline mt-3">
            <Link
              to={`/policies/add${clientLink}`}
              className="btn btn-info hstack gap-2 mb-3"
            >
              <i className="demo-psi-add fs-4"></i>
              <span className="vr"></span>
              Add Policy
            </Link>
            <FilterBar />
          </div>
        </>
      )}
      <div className="table-responsive bg-white">
        <table className="table table-striped">
          <TableHeader items={headers} />
          <tbody className="fs-5">
            {policies &&
              policies.map((policy) => (
                <PolicyWidget key={policy.id} policy={policy} />
              ))}
          </tbody>
        </table>
        <Paginate />
      </div>
    </>
  );
};

const PolicyWidget = ({ policy }) => {
  const { PolicyItems: policyItems } = policy;
  const { TableHeader } = useSortableTableHeader();
  const [show, setShow] = useState(false);

  const headers = [
    { label: "Company" },
    { label: "Created" },
    { label: "Info" },
    { label: "Expires" },
    { label: "Status", className: "text-center" },
    { label: "" },
  ];
  return (
    <>
      <tr onClick={() => setShow(!show)} role="button">
        <td className="d-flex">
          <div
            className="fs-1 me-3"
            onClick={() => setShow(!show)}
            role="button"
          >
            {show ? (
              <i className="bi bi-dash"></i>
            ) : (
              <i className="bi bi-plus"></i>
            )}
          </div>
          <div>
            <Link to={`/policies/${policy?.id}`} className="btn-link">
              {policy?.Client?.name}
            </Link>
            {policy?.title && <p className="mb-0">{policy?.title}</p>}
            {policy?.policyNumber && (
              <p className=" text-success">{policy?.policyNumber}</p>
            )}
          </div>
        </td>
        <td>
          <span className="fw-bold">{policy?.itemsCount}</span>
        </td>
        <td>
          <span className="fw-bold">{policy?.expiringCount}</span>
        </td>
        <td>
          <span className="fw-bold">{policy?.expiredCount}</span>
        </td>
        <td>{moment(policy?.createdAt).format("DD/MM/YYYY")}</td>
      </tr>
      {show && (
        <tr className="bg-white">
          <td colSpan={5}>
            <div className="table-responsive bg-white mb-2 ps-4">
              <table className="table table-bordered table-hover">
                <TableHeader items={headers} />
                <tbody>
                  {policyItems &&
                    policyItems?.map((item) => (
                      <tr key={item?.id}>
                        <td>
                          {item?.Company?.name && (
                            <>
                              <span>{item?.Company?.name}</span> <br />
                            </>
                          )}
                          {item?.Branch?.name && (
                            <>
                              <span>{item?.Branch?.name}</span>
                              <br />
                            </>
                          )}
                          <span>
                            {item?.PolicyType?.name} - {item?.Product?.name}
                          </span>
                        </td>
                        <td>{moment(item.createdAt).format("DD/MM/YYYY")}</td>
                        <td>
                          {item?.title && (
                            <>
                              <span className="text-muted">{item?.title}</span>
                              <br />
                            </>
                          )}

                          {item?.policyNumber && (
                            <span className="text-primary">
                              {item?.policyNumber}
                            </span>
                          )}
                          <br />
                          {item?.premium && (
                            <span className="fw-bold text-info">
                              Premium:{item.policyCurrency}{" "}
                              {Number(item.premium).toLocaleString("en-US")}
                            </span>
                          )}
                        </td>
                        <td>
                          {activeStatus.includes(item.status) && (
                            <>
                              <span>
                                {moment(item?.expires).format("DD/MM/YYYY") ||
                                  ""}
                              </span>
                              <p className="text-muted">
                                {item?.expires &&
                                  moment().to(moment(item?.expires))}
                              </p>
                            </>
                          )}
                        </td>
                        <td className="text-center fs-5">
                          <PolicyItemStatus status={item.status} />
                        </td>
                        <td className="text-center">
                          <div className="d-flex flex-wrap justify-content-center">
                            <div className="btn-group">
                              {/* {policy.canUnderwrite && ( */}
                              <Link
                                to={`/policies/${policy.id}/item/${item.id}`}
                                title="View policy"
                                className="btn btn-icon btn-outline-primary"
                              >
                                <i className="bi bi-pencil-square fs-4 text-success"></i>
                              </Link>
                              {/* )} */}
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

export default PolicyList;
